import get from "lodash-es/get";
import isEmpty from "lodash-es/isEmpty";
import { useCallback, useEffect, useState } from "react";

/**
 * State manager for expanding rows
 * expanded state is wiped on data change
 *
 *
 * @template T
 * @param {T[]} data
 * @param {string} uniqueKeyAccessor path to unique data property
 * @param {T[]} [preExpandRowsData] row data that will be initially expanded
 * @returns
 */
export const useExpand = <T>(data: T[], uniqueKeyAccessor: string, preExpandRowsData?: T[]) => {
  const [expandedRows, setExpanded] = useState<any[]>([]);

  const toggleExpandRow = useCallback(
    (rowData: T) => {
      const rowKey = get(rowData, uniqueKeyAccessor);
      const rowIndex = expandedRows.findIndex(key => key === rowKey);
      if (rowIndex === -1) {
        setExpanded([...expandedRows, rowKey]);
      } else {
        setExpanded([...expandedRows.filter(key => key !== rowKey)]);
      }
    },
    [expandedRows],
  );

  const toggleExpandAll = useCallback(() => {
    if (isAllExpanded()) {
      setExpanded([]);
    } else {
      setExpanded(data.map(rowData => get(rowData, uniqueKeyAccessor)));
    }
  }, [data, expandedRows]);

  const isRowExpanded = useCallback(
    (rowData: T): boolean => {
      const rowKey = get(rowData, uniqueKeyAccessor);
      return expandedRows.find(key => key === rowKey) ? true : false;
    },
    [expandedRows],
  );

  const isAllExpanded = () => {
    if (isEmpty(data)) {
      return false;
    }

    return data.every(row => expandedRows.indexOf(get(row, uniqueKeyAccessor)) !== -1);
  };

  useEffect(() => {
    const rowKeys = preExpandRowsData ? preExpandRowsData.map(rowData => get(rowData, uniqueKeyAccessor)) : null;
    setExpanded(rowKeys || []);
  }, [data]);

  return {
    allExpanded: isAllExpanded(),
    isRowExpanded,
    toggleExpandRow,
    toggleExpandAll,
  };
};
